// Geo / distance utilities — shared by the API, the manager bot, the mobile
// app, and the admin hub so the "too far from the patient" rule lives in
// exactly one place.
/** Mean Earth radius in feet (3958.8 mi × 5280 ft/mi). */
const EARTH_RADIUS_FEET = 20_902_464;
/** Feet per meter (Expo's `coords.accuracy` is reported in meters). */
const FEET_PER_METER = 3.280839895;
/**
 * Threshold (in feet) beyond which a nurse's clock-in/out GPS point is
 * considered "too far" from the patient's address. Single source of truth —
 * tune this constant rather than sprinkling magic numbers across apps.
 */
export const CLOCK_DISTANCE_THRESHOLD_FEET = 400;
/**
 * Upper bound (in feet) past which a distance is almost certainly a bad GPS fix
 * (a stale/cell-tower reading miles off), not a nurse who is genuinely "a bit
 * far". Beyond this we mark the point "unreliable" and do NOT raise the red
 * "too far" flag — a nurse can't drive 1+ mile away and back while at the home.
 * 1 mile = 5280 ft.
 */
export const CLOCK_DISTANCE_UNRELIABLE_FEET = 5280;
/**
 * Radius (in feet) within which a point from the visit activity trail counts as
 * PRESENCE at the patient's home.
 *
 * Deliberately looser than {@link CLOCK_DISTANCE_THRESHOLD_FEET}, and the two
 * answer different questions. The clock threshold asks "is this reading tight
 * enough to accept without comment", where being strict costs a nurse only a
 * prompt. This one asks "do we have evidence she was at the home", where being
 * strict costs her a pay hold — and a rooftop geocode on an apartment block can
 * sit several hundred feet from where a person actually stands (visit 421714 in
 * production: stationary at 563 ft for 35 minutes, at a ROOFTOP-precision
 * address). Presence is a question about the building, not about the doorstep.
 */
export const TRAIL_PRESENCE_RADIUS_FEET = 1000;
/**
 * How far our stored geocode may disagree with a fresh one before we stop
 * trusting our own address data. Past this we treat the mismatch as OUR bug and
 * never hold a nurse's pay on it: in the September 2026 trail cohort exactly two
 * of 179 addresses were wrong, and both had produced a "she was a mile away"
 * reading for a nurse who was in fact 66 ft and 173 ft from the real door.
 */
export const GEOCODE_DISAGREEMENT_FEET = 400;
/** Great-circle (haversine) distance between two coordinates, in feet. */
export function distanceFeet(a, b) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const sinLat = Math.sin(dLat / 2);
    const sinLng = Math.sin(dLng / 2);
    const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
    return EARTH_RADIUS_FEET * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}
/** Convert a meters value to feet (e.g. a GPS accuracy radius). */
export function metersToFeet(meters) {
    return meters * FEET_PER_METER;
}
function isUsableCoord(p) {
    return (p != null &&
        Number.isFinite(p.lat) &&
        Number.isFinite(p.lng) &&
        // Reject the "null island" (0,0) sentinel that bad geocodes produce.
        !(p.lat === 0 && p.lng === 0));
}
/**
 * Accuracy-aware "too far" decision. We flag only when the distance, after
 * subtracting the GPS accuracy radius (the most generous reading for the
 * nurse), still exceeds the threshold — so a noisy fix in a dense urban canyon
 * doesn't false-flag a nurse who is actually standing at the home. Missing or
 * invalid coordinates ⇒ not far (a neutral "unknown", never a red flag).
 */
export function computeClockDistance(input) {
    const accuracyFeet = input.accuracyFeet != null ? input.accuracyFeet : null;
    if (!isUsableCoord(input.nurse) || !isUsableCoord(input.patient)) {
        return { distanceFeet: null, accuracyFeet, isFar: false, unreliable: false };
    }
    const dist = distanceFeet(input.nurse, input.patient);
    const margin = accuracyFeet != null && accuracyFeet > 0 ? accuracyFeet : 0;
    const adjusted = dist - margin;
    // Beyond the unreliable cap it's a bad fix, not a real "far" — don't red-flag.
    const unreliable = adjusted > CLOCK_DISTANCE_UNRELIABLE_FEET;
    const isFar = adjusted > CLOCK_DISTANCE_THRESHOLD_FEET && !unreliable;
    return { distanceFeet: Math.round(dist), accuracyFeet, isFar, unreliable };
}
/** Convenience boolean wrapper around {@link computeClockDistance}. */
export function isClockLocationFar(input) {
    return computeClockDistance(input).isFar;
}
/**
 * Is this reading beyond the threshold at all, ignoring the unreliable cap?
 *
 * The difference from {@link isClockLocationFar} is only what happens past a
 * mile. `isFar` deliberately goes quiet there so a wildly bad fix does not raise
 * a red badge. This one does not, and the two exist because they answer
 * different questions:
 *
 *   - `isFar` decides whether to show a RED FLAG on an admin surface. Being
 *     wrong there is cosmetic, so it errs quiet.
 *   - this decides whether the nurse is WARNED AND ASKED FOR A REASON, and
 *     (server-side) whether her pay waits for a human. Being wrong there costs
 *     her money, so it must not go quiet exactly when the distance is largest.
 *
 * They must stay in step: every reading that can hold a nurse's pay is a reading
 * she was warned about at the time and had the chance to explain. Using `isFar`
 * for the prompt while the money side used the uncapped rule meant a nurse 1.7
 * miles out saw nothing at all, clocked in normally, and found her pay held
 * days later over a distance no one had mentioned to her.
 */
export function isBeyondClockThreshold(input) {
    const result = computeClockDistance(input);
    if (result.distanceFeet == null) {
        return false;
    }
    const margin = result.accuracyFeet != null && result.accuracyFeet > 0 ? result.accuracyFeet : 0;
    return result.distanceFeet - margin > CLOCK_DISTANCE_THRESHOLD_FEET;
}
//# sourceMappingURL=geo.js.map